import { Alert, Divider } from '@mui/material';
import { Box } from '@mui/system';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AuthFormHeader from '../auth-form-header';
import AuthFormWrapper from '../../templates/auth-form-wrapper';
import { EmailStepFormValues } from '../../templates/login/schema/login-schema';

import { lightActions } from '@/colors';
import { InputField } from '@/components/atoms/input-field';
import { LoadingButton } from '@/components/atoms/loading-button/loading-button';
import { useKeyDown } from '@/hooks/use-key-down';
import { Logo } from '@/icons/logo';

interface Props {
  onSubmit: (values: EmailStepFormValues) => Promise<void>;
  onAuth0Login: () => Promise<void>;
  isAuth0Submitting: boolean;
}

export const EmailStep = ({ onSubmit, onAuth0Login, isAuth0Submitting }: Props) => {
  const { t } = useTranslation(['auth', 'common']);
  const { control, handleSubmit } = useFormContext<EmailStepFormValues>();
  const { isValid, isSubmitting, errors } = useFormState<EmailStepFormValues>();

  useKeyDown('Enter', handleSubmit(onSubmit));

  return (
    <AuthFormWrapper>
      <AuthFormHeader
        title={t('auth:login.title')}
        description={t('auth:login.contentEmail')}
        icon={<Logo />}
      />
      <Box>
        <Controller
          name="email"
          control={control}
          render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
            <InputField
              label={t('auth:email')}
              fullWidth
              required
              inputRef={ref}
              onBlur={onBlur}
              onChange={onChange}
              type="email"
              value={value}
              autoComplete="email"
              error={!!error}
              helperText={error?.message}
              sx={{ my: 1 }}
            />
          )}
        />

        {errors?.root?.message ? <Alert severity="error">{errors.root.message}</Alert> : null}
      </Box>

      <LoadingButton
        variant="contained"
        size="medium"
        disabled={!isValid}
        loading={isSubmitting || isAuth0Submitting}
        onClick={handleSubmit(onSubmit)}>
        {t('auth:login.submitButton')}
      </LoadingButton>

      <Divider sx={{ borderColor: lightActions.disabledBackground }}>{t('common:or')}</Divider>
      <LoadingButton
        variant="outlined"
        size="medium"
        loading={isAuth0Submitting}
        disabled={isSubmitting}
        onClick={onAuth0Login}>
        {t('auth:login.oldLoginMethod')}
      </LoadingButton>
    </AuthFormWrapper>
  );
};
